import { Button, Css, Icon, Palette, TextField, useTestIds } from "@homebound/beam";
import { observer } from "mobx-react";
import { useCallback, useMemo, useState } from "react";
import { findBestLocationMatch } from "../hooks/useFuzzyMatch";
import { useInsideMapsConfig } from "../hooks/useInsideMapsConfig";
import { LocationOption, LocationSelectionMode, TliLocationItem } from "../types";

interface LocationSelectorProps {
  locations: LocationOption[];
  tliLocationItems: TliLocationItem[];
}

export const LocationSelector = observer(function LocationSelector({
  locations,
  tliLocationItems,
}: LocationSelectorProps) {
  const store = useInsideMapsConfig();
  const tid = useTestIds({});
  const [mode, setMode] = useState<LocationSelectionMode>("auto");
  const [hardcodedIds, setHardcodedIds] = useState("");

  const part = store.selectedPart;
  const cameraId = store.selectedCameraId;

  const selectedLocationIds = new Set(part?.locationIds ?? []);

  // Filter locations by selected materials
  const selectedMvIds = new Set(part?.textures.flatMap((t) => t.materialVariantIds ?? []) ?? []);

  const filteredLocations = useMemo(() => {
    if (selectedMvIds.size === 0) return locations;
    const locationIdsWithMaterials = new Set(
      tliLocationItems.filter((item) => selectedMvIds.has(item.materialVariantId)).map((item) => item.locationId),
    );
    return locations.filter((l) => locationIdsWithMaterials.has(l.id));
  }, [locations, tliLocationItems, selectedMvIds]);

  if (!part || !cameraId) {
    return null;
  }

  return (
    <div css={Css.my2.p3.bgWhite.br8.ba.bcBlue200.$} {...tid.locationSelector}>
      <div css={Css.smSb.mb1.blue700.$}>Location Assignment for {part.name}</div>

      {/* Selection Mode */}
      <div css={Css.xs.gray600.mb1.$}>Selection Mode:</div>
      <div css={Css.df.gap1.mb2.$}>
        <ModeTab label="Auto (Material-based)" active={mode === "auto"} onClick={() => setMode("auto")} />
        <ModeTab label="Manual (All Locations)" active={mode === "manual"} onClick={() => setMode("manual")} />
        <ModeTab label="Hardcoded (Direct Input)" active={mode === "hardcoded"} onClick={() => setMode("hardcoded")} />
      </div>

      {/* Mode description */}
      <div css={Css.xs.gray500.mb2.$}>
        {mode === "auto" && "Shows locations based on materials assigned to textures"}
        {mode === "manual" && "Shows all available locations for manual selection"}
        {mode === "hardcoded" && "Enter location IDs directly as comma-separated values"}
      </div>

      {mode === "auto" && (
        <AutoLocationSelector
          cameraId={cameraId}
          partId={part.id}
          partName={part.name}
          locations={filteredLocations}
          selectedLocationIds={selectedLocationIds}
        />
      )}
      {mode === "manual" && (
        <ManualLocationSelector
          cameraId={cameraId}
          partId={part.id}
          locations={filteredLocations}
          selectedLocationIds={selectedLocationIds}
        />
      )}
      {mode === "hardcoded" && (
        <div css={Css.df.fdc.gap1.$}>
          <TextField
            label="Location IDs (comma-separated)"
            value={hardcodedIds}
            onChange={(val) => setHardcodedIds(val ?? "")}
            placeholder="l:123, l:456"
          />
          <Button
            label="Apply"
            onClick={() => {
              store.setHardcodedLocationIds(cameraId, part.id, hardcodedIds);
              setHardcodedIds("");
            }}
          />
        </div>
      )}

      {/* Selected locations display */}
      {selectedLocationIds.size > 0 && (
        <div css={Css.mt2.$}>
          <div css={Css.xs.gray600.mb1.$}>Selected Locations:</div>
          {Array.from(selectedLocationIds).map((locId) => {
            const loc = locations.find((l) => l.id === locId);
            return (
              <div key={locId} css={Css.df.aic.jcsb.xs.py1.px2.bgWhite.br4.mb1.$}>
                <span css={Css.blue700.$}>{loc ? `${loc.path || loc.name}` : locId}</span>
                <button
                  css={Css.cursorPointer.add("background", "none").add("border", "none").$}
                  onClick={() => store.togglePartLocation(cameraId, part.id, locId)}
                >
                  <Icon icon="x" inc={2} color={Palette.Red500} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

const AutoLocationSelector = observer(function AutoLocationSelector({
  cameraId,
  partId,
  partName,
  locations,
  selectedLocationIds,
}: {
  cameraId: string;
  partId: string;
  partName: string;
  locations: LocationOption[];
  selectedLocationIds: Set<string>;
}) {
  const store = useInsideMapsConfig();

  const bestMatch = useMemo(() => findBestLocationMatch(partName, locations), [partName, locations]);

  const handleToggleLocation = useCallback(
    (locId: string) => store.togglePartLocation(cameraId, partId, locId),
    [store, cameraId, partId],
  );

  return (
    <div>
      {locations.length > 0 ? (
        <div css={Css.maxhPx(200).oa.$}>
          {locations.map((loc) => {
            const isSelected = selectedLocationIds.has(loc.id);
            const isBestMatch = bestMatch?.id === loc.id;
            return (
              <div
                key={loc.id}
                css={Css.df.aic.jcsb.pyPx(6).pxPx(8).br4.cursorPointer.sm.if(isSelected).bgBlue50.ba.bcBlue200.$}
                onClick={() => handleToggleLocation(loc.id)}
              >
                <div css={Css.add("minWidth", "0").$}>
                  <div css={Css.sm.$}>{loc.path || loc.name}</div>
                  <div css={Css.xs.gray500.$}>{loc.name}</div>
                </div>
                <div css={Css.df.aic.gap1.$}>
                  {isBestMatch && <span css={Css.xs.blue600.$}>Best match</span>}
                  {isSelected && <Icon icon="check" inc={2.5} color={Palette.Blue600} />}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div css={Css.xs.gray500.$}>No matching locations found for "{partName}"</div>
      )}
    </div>
  );
});

const ManualLocationSelector = observer(function ManualLocationSelector({
  cameraId,
  partId,
  locations,
  selectedLocationIds,
}: {
  cameraId: string;
  partId: string;
  locations: LocationOption[];
  selectedLocationIds: Set<string>;
}) {
  const store = useInsideMapsConfig();
  const [search, setSearch] = useState("");

  const filtered = search
    ? locations.filter(
        (l) =>
          l.name.toLowerCase().includes(search.toLowerCase()) || l.path.toLowerCase().includes(search.toLowerCase()),
      )
    : locations;

  return (
    <div>
      <TextField
        label="Search locations..."
        labelStyle="hidden"
        value={search}
        onChange={(val) => setSearch(val ?? "")}
        placeholder="Search locations..."
      />
      <div css={Css.maxhPx(200).oa.mt1.$}>
        {filtered.map((loc) => {
          const isSelected = selectedLocationIds.has(loc.id);
          return (
            <div
              key={loc.id}
              css={Css.df.aic.jcsb.pyPx(6).pxPx(8).br4.cursorPointer.sm.if(isSelected).bgBlue50.ba.bcBlue200.$}
              onClick={() => store.togglePartLocation(cameraId, partId, loc.id)}
            >
              <div>
                <div css={Css.sm.$}>{loc.path || loc.name}</div>
                <div css={Css.xs.gray500.$}>{loc.name}</div>
              </div>
              {isSelected && <Icon icon="check" inc={2.5} color={Palette.Blue600} />}
            </div>
          );
        })}
      </div>
    </div>
  );
});

function ModeTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      css={
        Css.pyPx(6)
          .pxPx(14)
          .br4.sm.cursorPointer.if(active)
          .white.add("background", "#2563eb")
          .add("border", "1px solid #2563eb")
          .if(!active)
          .gray700.bgWhite.add("border", "1px solid #d1d5db").$
      }
      onClick={onClick}
    >
      {label}
    </button>
  );
}
